import { IFormRender } from '@busybusy/webapp-react-ui';
import { IBitwisePayload } from 'components/foundation/BitwiseGroup/BitwiseGroup';
import { bitwisePayloadMasker } from 'components/foundation/BitwiseGroup/util/Util';
import ConfirmationDialog from 'components/foundation/dialogs/ConfirmationDialog/ConfirmationDialog';
import BitwiseGroupFormField from 'components/foundation/form-fields/BitwiseGroupFormField/BitwiseGroupFormField';
import usePhotosViewCollectionPermissionEnabled from 'containers/photos/hooks/usPhotosViewCollectionPermissionEnabled/usePhotosViewCollectionPermissionEnabled';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import SectionHeader from 'containers/settings/components/SectionHeader/SectionHeader';
import PositionProgressFormFields from 'containers/settings/positions/components/PositionProgressFormFields';
import PositionsViewActivityReportFormFields from 'containers/settings/positions/components/PositionsViewActivityReportFormFields';
import { defaultBitwisePayloads } from 'containers/settings/positions/util/constants';
import { isEveryonePosition, POSITION_MASK_EVERYONE } from 'containers/settings/positions/util/util';
import { useOpenable } from 'hooks';
import useProjectPlanningIsEnabled from 'hooks/feature/project-planning/useProjectPlanningIsEnabled/useProjectPlanningIsEnabled';
import type { IOpenableHook } from 'hooks/utils/useOpenable';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import { ICreatePositionsFormData } from '../CreatePositionForm';

export interface ICreatePositionSupervisionSectionProps {
  form: IFormRender<ICreatePositionsFormData>;
}

interface ISupervisorSetting {
  name: string;
  title: string;
  detail: React.ReactNode;
  bitwisePayloads: IBitwisePayload[];
  onChange?: (value: number) => void;
}

const CreatePositionSupervisionSection = ({ form }: ICreatePositionSupervisionSectionProps) => {
  const progressReportIsEnabled = useFeatureFlags('WORK_ACCOMPLISHED');
  const isLocationFlaggingEnabled = useFeatureFlags('LOCATION_FLAGGING');
  const isPerDiemEnabled = useFeatureFlags('PER_DIEM');
  const photosViewCollectionPermissionEnabled = usePhotosViewCollectionPermissionEnabled();
  const isProjectPlanningEnabled = useProjectPlanningIsEnabled();
  const projectPlanManagementConfirmationDialog = useOpenable();

  const supervisionSettings: ISupervisorSetting[] = [];

  supervisionSettings.push({
    name: 'manageEmployees',
    title: t('Manage Employees for') + ':',
    detail: t('View, create, edit, and archive employees; set wages, position, etc.'),
    bitwisePayloads: defaultBitwisePayloads,
  });

  supervisionSettings.push({
    name: 'timeEvents',
    title: t('Track Time for') + ':',
    detail: t('Clock in, clock out, start/end breaks, and switch.'),
    bitwisePayloads: bitwisePayloadMasker(defaultBitwisePayloads, form.state.data.manageTimeEntries ?? 0),
  });

  supervisionSettings.push({
    name: 'manageTimeEntries',
    title: t('Edit Time Entries for') + ':',
    detail: t(
      'View, create, edit, and delete time entries; clock in at, clock out at, switch at; add, edit, delete, and approve time off.'
    ),
    bitwisePayloads: defaultBitwisePayloads,
    onChange: (value: number) => {
      const current = form.state.data.timeEvents;
      const newValue = current | value;
      if (newValue !== current) {
        form.handleChange('timeEvents', newValue);
      }
    },
  });

  supervisionSettings.push({
    name: 'manageTimeOff',
    title: t('Manage Time Off for') + ':',
    detail: t('Can add, edit, delete, and approve time off.'),
    bitwisePayloads: defaultBitwisePayloads,
  });

  supervisionSettings.push({
    name: 'manageMediaCollection',
    title: t('Manage Photos Posted by') + ':',
    detail: t('Can edit and delete photos, notes, comments, and tags uploaded by other employees.'),
    bitwisePayloads: defaultBitwisePayloads,
    onChange: (value: number) => {
      const currentViewPhotos = form.state.data.viewMediaCollection;
      const newValue = currentViewPhotos | value;
      if (newValue !== currentViewPhotos) {
        form.handleChange('viewMediaCollection', newValue);
      }
    },
  });

  if (photosViewCollectionPermissionEnabled) {
    supervisionSettings.push({
      name: 'viewMediaCollection',
      title: t('View Photos Posted by') + ':',
      detail: t('Can view photos and notes.'),
      bitwisePayloads: bitwisePayloadMasker(defaultBitwisePayloads, (form.state.data.manageMediaCollection ?? 0) | 1),
    });
  }

  supervisionSettings.push({
    name: 'memberLock',
    title: t('Manage Lock Dates for') + ':',
    detail: t('View, set, edit, and remove employee lock dates.'),
    bitwisePayloads: defaultBitwisePayloads,
  });

  supervisionSettings.push({
    name: 'viewSchedule',
    title: t('View Schedules for') + ':',
    detail: t('View schedule items.'),
    bitwisePayloads: bitwisePayloadMasker(defaultBitwisePayloads, form.state.data.manageSchedule ?? 0),
  });

  supervisionSettings.push({
    name: 'manageSchedule',
    title: t('Manage Schedule for') + ':',
    detail: t('View, create, edit, delete schedule items.'),
    bitwisePayloads: defaultBitwisePayloads,
    onChange: (value: number) => {
      const currentViewSchedule = form.state.data.viewSchedule;
      const newValue = currentViewSchedule | value;
      if (newValue !== currentViewSchedule) {
        form.handleChange('viewSchedule', newValue);
      }

      // warn the user that they will disable project plan management if they remove manage schedule permissions
      if (isProjectPlanningEnabled && !isEveryonePosition(value) && form.state.data.manageResourcePlanning === true) {
        projectPlanManagementConfirmationDialog.open();
        return false;
      }
    },
  });

  if (isLocationFlaggingEnabled) {
    supervisionSettings.push({
      name: 'manageGpsApproval',
      title: t('Authorize GPS for') + ':',
      detail: t('Can approve or unauthorize employee locations outside project boundaries.'),
      bitwisePayloads: defaultBitwisePayloads,
    });
  }

  if (isPerDiemEnabled) {
    supervisionSettings.push({
      name: 'managePerDiem',
      title: t('Manage Per Diem for') + ':',
      detail: t('Can add, edit, delete, and approve per diem for others.'),
      bitwisePayloads: defaultBitwisePayloads,
    });
  }

  return (
    <>
      <SectionHeader className="mb-4">{t('Supervision Settings')}</SectionHeader>

      {supervisionSettings.map(({ name, title, detail, bitwisePayloads: payloads, onChange }) => (
        <>
          <HeaderText title={title}>{detail}</HeaderText>
          <BitwiseGroupFormField className="mt-2" name={name} payloads={payloads} form={form} onChange={onChange} />
        </>
      ))}

      {progressReportIsEnabled && <PositionProgressFormFields form={form} />}

      <PositionsViewActivityReportFormFields form={form} />

      <DisableProjectPlanManagementConfirmationDialog form={form} dialog={projectPlanManagementConfirmationDialog} />
    </>
  );
};

interface IDisableProjectPlanManagementConfirmationDialogProps {
  form: IFormRender<ICreatePositionsFormData>;
  dialog: IOpenableHook;
}
function DisableProjectPlanManagementConfirmationDialog(props: IDisableProjectPlanManagementConfirmationDialogProps) {
  const { form, dialog } = props;

  const message = t(`
    To manage project plans, the user must be able to edit schedules for all positions.
    If you remove any of the permissions for managing schedules, the Manage Project Plans permission will be unchecked automatically.
    Are you sure you want to continue?
  `);

  function handleConfirm() {
    // if the user confirms, disable project plan management
    form.handleChange('manageResourcePlanning', false);
    dialog.close();
  }

  function handleCancel() {
    // if the user cancels, revert manage schedule permissions to full access
    form.handleChange('manageSchedule', POSITION_MASK_EVERYONE);
    dialog.close();
  }

  return (
    <ConfirmationDialog
      title={t(`Disable Project Plan Management`)}
      isOpen={dialog.isOpen}
      message={message}
      confirmButtonText={t('Disable')}
      closeButtonText={t('Cancel')}
      onConfirm={handleConfirm}
      onClose={handleCancel}
    />
  );
}

export default CreatePositionSupervisionSection;
